import React, { useEffect, useRef, useState } from "react";

import {
  KeyedEntry,
  isNullOrUndefined,
  isUniquePurchaserName,
  isValidNonnegativeNumber,
  isValidPostCodeFormat,
  queryIsLoading,
  searchPostCode,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingCropPurchasers,
  useGetFootprintingCropSales,
  useKeyedFormList,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingCropSales,
  useTranslation,
} from "utils";

import { TRANSPORT_COUNTRY_CODE } from "consts";

import { CropSaleData, PurchaserData } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  Input,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tooltip,
  Typography,
} from "components";

const createBlankCropSale = (farmId: string): CropSaleData => ({
  purchaser: {
    farmId,
    name: null,
    postCode: null,
    countryCode: null,
  },
  soldYieldTs: null,
});

interface CropTransportFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
}

const CropTransportForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropTransportFormProps) => {
  const { t } = useTranslation("footprintingCrop");
  const formRef = useRef<FormInstance>(null);

  const [cropSaleActive, setCropSaleActive] = useState<boolean | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const purchasersQuery = useGetFootprintingCropPurchasers(farmId);
  const cropSalesQuery = useGetFootprintingCropSales(primaryDataId ?? "");
  const isLoading = [cropPrimaryDataQuery, cropSalesQuery, purchasersQuery].some(queryIsLoading);
  const keyedCropSales = useKeyedFormList<CropSaleData>(formRef, { isReady: !isLoading });

  useEffect(() => {
    if (purchasersQuery.data && cropSalesQuery.data && primaryDataId) {
      const existingEntries = cropSalesQuery.data.map((sale) => {
        const purchaserMatch = purchasersQuery.data?.find((purchaser) => purchaser.id === sale.purchaserId) as PurchaserData | undefined;

        return {
          id: sale.id,
          purchaserId: sale.purchaserId ?? "",
          purchaser: {
            id: sale.purchaserId ?? "",
            farmId: farmId,
            name: purchaserMatch?.name ?? null,
            postCode: purchaserMatch?.postCode ?? null,
            countryCode: purchaserMatch?.countryCode ?? null,
          },
          soldYieldTs: sale.soldYieldTs,
        };
      });

      keyedCropSales.actions.hydrate(existingEntries.length > 0 ? existingEntries : [createBlankCropSale(farmId)]);
    }
  }, [purchasersQuery.data, cropSalesQuery.data, primaryDataId, farmId, keyedCropSales.actions]);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setCropSaleActive(cropPrimaryDataQuery.data.cropSaleActive ?? null);

      formRef.current?.setFieldsValue({
        cropSaleActive: cropPrimaryDataQuery.data.cropSaleActive ?? null,
      });

      if (cropPrimaryDataQuery.data.cropSaleActive !== null) {
        keyedCropSales.actions.requestRehydrationValidation();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, keyedCropSales.actions]);

  const { mutate: mutateCropSales } = useMutateFootprintingCropSales(
    farmId,
    cropId,
    year,
    {
      footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
      cropSales: keyedCropSales.payload,
      farmId,
    },
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      cropSaleActive,
    },
    primaryDataId
  );

  const updatePurchaserField = (sale: KeyedEntry<CropSaleData>, field: keyof PurchaserData, value: string | number) =>
    keyedCropSales.actions.update(sale.clientKey, { purchaser: { ...sale.purchaser, [field]: value } });

  const addCropSale = () => {
    if (primaryDataId) keyedCropSales.actions.add(createBlankCropSale(farmId));
  };

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropSales();
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateCropSales, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-crop-transport-form" className="padding-100">
      <Form ref={formRef} preserve={false}>
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
            {t("cropTransportForm.question.radioQuestion")}
            {true && <span className="required-asterisk">*</span>}
            <Tooltip title={t("cropTransportForm.tooltips.radioQuestion")}>
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Tooltip>
          </Typography>
          <KlimRadioGroup
            required
            name="cropSaleActive"
            dataTestId="footprinting-crop-transport-form-radio"
            vertical={false}
            disabled={readOnly}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setCropSaleActive(target.value);
            }}
            value={cropSaleActive}
            defaultValue={cropSaleActive}
            optionType="button"
            options={[true, false].map((opt) => ({
              label: t(`footprinting:radioAnswer.${opt}`),
              value: opt,
            }))}
          />
        </Flex>
        {cropSaleActive &&
          keyedCropSales.entries.map((sale, index) => (
            <Flex key={sale.clientKey} vertical gap={16}>
              <Flex justify="space-between" align="center" vertical={false}>
                <Typography variant="h5" style={{ flex: 1 }}>
                  {t("cropTransportForm.purchaser") + (index + 1)}
                </Typography>
                {keyedCropSales.entries.length > 1 && (
                  <IconButton
                    type="tertiary"
                    size="small"
                    iconName="tabler/trash"
                    dataTestId={`footprinting-crop-transport-form-delete-${index}`}
                    onClick={() => keyedCropSales.actions.remove(sale.clientKey)}
                  />
                )}
              </Flex>
              <Input
                className="floating-label"
                dataTestId={`footprinting-crop-transport-form-name-input-${index}`}
                disabled={readOnly}
                name={`purchaserName-${sale.clientKey}`}
                label={t("cropTransportForm.question.purchaserNameQuestionLabel")}
                tooltip={t("cropTransportForm.tooltips.purchaserNameQuestionLabel")}
                value={sale.purchaser?.name ?? ""}
                initialValue={sale.purchaser?.name ?? ""}
                onChange={(e) => updatePurchaserField(sale, "name", e.target.value)}
                message={t("cropTransportForm.error.purchaserNameQuestionError")}
                rules={[
                  { required: true },
                  {
                    validator: (_: unknown, value: string) =>
                      isUniquePurchaserName(keyedCropSales.entries, value, index)
                        ? Promise.resolve()
                        : Promise.reject(new Error("Purchaser name must be unique")),
                  },
                ]}
              />
              <Select
                className="floating-label"
                disabled={readOnly}
                dataTestId={`footprinting-crop-transport-form-country-select-${index}`}
                name={`country-${sale.clientKey}`}
                label={t("cropTransportForm.question.countryQuestionLabel")}
                tooltip={t("cropTransportForm.tooltips.countryQuestionLabel")}
                value={sale.purchaser?.countryCode}
                initialValue={sale.purchaser?.countryCode}
                onChange={(value) => updatePurchaserField(sale, "countryCode", value || null)}
                allowClear
                rules={[{ required: true, message: t("cropTransportForm.error.countryQuestionError") }]}
                dropdownStyle={{ zIndex: 10010 }}
              >
                {Object.entries(TRANSPORT_COUNTRY_CODE).map(([name, code]) => (
                  <Option disabled={readOnly} key={`country-select-${index}-${code}`} label={t(name)} value={code}>
                    {t(`enums:countries.${code}`)}
                  </Option>
                ))}
              </Select>
              <Input
                className="floating-label"
                dataTestId={`footprinting-crop-transport-form-postcode-input-${index}`}
                name={`postcode-${sale.clientKey}`}
                disabled={readOnly}
                label={t("cropTransportForm.question.postcodeQuestionLabel")}
                tooltip={t("cropTransportForm.tooltips.postcodeQuestionLabel")}
                value={sale.purchaser?.postCode ?? ""}
                initialValue={sale.purchaser?.postCode ?? ""}
                onChange={(e) => updatePurchaserField(sale, "postCode", e.target.value)}
                message={t("cropTransportForm.error.postcodeQuestionError")}
                rules={[
                  { required: true },
                  {
                    validator: async (_: unknown, value: string) => {
                      if (!value) return Promise.resolve();

                      if (!isValidPostCodeFormat(value, sale.purchaser.countryCode)) {
                        return Promise.reject(new Error(t("cropTransportForm.error.postcodeQuestionError")));
                      }

                      const searchResult = await searchPostCode(value, sale.purchaser.countryCode);
                      if (searchResult === null) return Promise.reject(new Error(t("cropTransportForm.error.postcodeQuestionError")));

                      return Promise.resolve();
                    },
                  },
                ]}
              />
              <InputNumber
                className="floating-label"
                name={`soldYieldTs-${sale.clientKey}`}
                disabled={readOnly}
                label={t("cropTransportForm.question.shareQuestionLabel")}
                tooltip={t("cropTransportForm.tooltips.shareQuestionLabel")}
                dataTestId={`footprinting-crop-transport-form-yield-input-${index}`}
                suffix="t"
                value={sale.soldYieldTs}
                initialValue={sale.soldYieldTs}
                onChange={(v) => keyedCropSales.actions.update(sale.clientKey, { soldYieldTs: v ?? null })}
                validateFirst
                rules={[
                  { required: true, message: t("cropTransportForm.error.saleQuestionError") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("cropTransportForm.error.saleQuestionError")));
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              />
              {index === keyedCropSales.entries.length - 1 && (
                <Button type="text" disabled={readOnly} onClick={addCropSale}>
                  <Icon iconName="klim/plus" className="icon inline" />
                  {t("cropTransportForm.add")}
                </Button>
              )}
            </Flex>
          ))}
        <div />
      </Form>
    </Card>
  );
};

CropTransportForm.Key = "crop.transport";
export { CropTransportForm };
